import { Directive, inject, Input } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { ComponentAdapterParent } from "./adapters/component-adapter-parent";
import { SmartComponentService } from "src/components/shared-components/service/smart-component.service";
import { GenericCounterGraphData } from "./generic-counter-graph-data";
import { AlertService } from "src/service/alert.service";
import { LoaderService } from "src/service/loader.service";
import { SmartPaletteCounter } from "./smart-palette";
import { CounterConsumptionMonthlyComparison } from "./counter-consumption-monthly-comparison";
import { StaticClass } from "./general-classes/static-classes";
import { CounterConsumptionDailyPeaks } from "./counter-consumption-daily-peaks";
import { SmartComponent } from "./smart-components/smart-component";
import { ViewMode } from "../enum/enumViewMode";
import { CounterDistributionConsumption } from "./counter-distribution-consumption";

/**
 * Parent class for all counter widgets
 * The empty @Directive() is needed by the AOT build to pass these @Input to every counter widget.
 */
@Directive()
export abstract class WidgetCounterParent<T extends ComponentAdapterParent> {
  protected readonly _alertService = inject(AlertService);
  protected readonly _loaderService = inject(LoaderService);

  protected readonly _translateService = inject(TranslateService);
  private readonly _smartComponentService = inject(SmartComponentService);
  public startDate: Date;
  public endDate: Date;
  public startDateSummary: Date;
  public endDateSummary: Date;

  //Reference to component adapter for skill
  @Input() componentAdapter: T = null;
  @Input() smartPalette: SmartPaletteCounter = null;
  @Input() defaultDecimalDigits: number = 2;

  @Input() isExpanded: boolean = false;
  @Input() isSupervision: boolean = false;

  constructor() {
    this.setCurrentMonth();
  }

  /**
   * Set the current month as start and end date
   */
  private setCurrentMonth() {
    const currentDate = new Date();
    this.setPeriodByDate(currentDate);
    this.setSummaryPeriodByDate(currentDate);
  }

  /**
   * Set the period by date
   * @param date
   */
  public setSummaryPeriodByDate(date: Date) {
    const currentYear = date.getFullYear();
    const currentMonth = date.getMonth(); // 0-11

    this.startDateSummary = this.createStartDate(currentYear, currentMonth);
    this.endDateSummary = this.createEndDate(currentYear, currentMonth);
  }

  /**
   * Set the period by date
   * @param date
   */
  public setPeriodByDate(date: Date) {
    const currentYear = date.getFullYear();
    const currentMonth = date.getMonth(); // 0-11

    this.startDate = this.createStartDate(currentYear, currentMonth);
    this.endDate = this.createEndDate(currentYear, currentMonth);
  }

  /**
   * Rounds the chart data based on the type of chart.
   * @param chartData The data to round.
   * @param isBarChart Indicates if the data is for a bar chart.
   * @returns The rounded chart data.
   */
  public roundChartData(
    chartData: GenericCounterGraphData,
    isBarChart: boolean
  ): GenericCounterGraphData {
    if (Array.isArray(chartData.data)) {
      chartData.data.forEach((item) => {
        if (isBarChart) {
          let castedItem = item as CounterConsumptionMonthlyComparison;

          if (castedItem.month !== undefined) {
            castedItem.lastYearValue = this.roundValue(
              castedItem.lastYearValue
            );
            castedItem.currentYearValue = this.roundValue(
              castedItem.currentYearValue
            );
          }
        } else {
          let castedItem = item as CounterConsumptionDailyPeaks;

          if (castedItem.valueMax !== undefined) {
            castedItem.valueMax = this.roundValue(castedItem.valueMax);
            castedItem.valueMin = this.roundValue(castedItem.valueMin);
          }
        }
      });
    }
    return chartData;
  }

  /**
   * Rounds a given value using the static method.
   * @param value The value to round.
   * @returns The rounded value.
   */
  private roundValue(value: string): string {
    return StaticClass.formatDecimalValue(value, this.defaultDecimalDigits);
  }

  /**
   * Create start date for the given year and month
   */
  private createStartDate(year: number, month: number): Date {
    return new Date(Date.UTC(year, month, 1));
  }

  /**
   * Create end date for the given year and month
   */
  private createEndDate(year: number, month: number): Date {
    const endOfMonth = new Date(year, month + 1, 0);
    return new Date(
      endOfMonth.getFullYear(),
      endOfMonth.getMonth(),
      endOfMonth.getDate(),
      23,
      59,
      59
    );
  }

  public async ngOnInit() {
    this.onComponentLoad();
  }

  public ngOnDestroy() {
    this.onComponentDestroy();
  }

  /**
   * Get peaks data
   * @param address
   * @param instanceGUID
   * @returns
   */
  protected async getPeaksData(
    address: string,
    instanceGUID: string
  ): Promise<GenericCounterGraphData> {
    return await this._smartComponentService.getPeaksData(
      address,
      instanceGUID,
      this.startDate.toISOString(),
      this.endDate.toISOString()
    );
  }

  /**
   * Get peaks data csv
   * @param address
   * @param instanceGUID
   * @returns
   */
  protected async getPeaksDataCSV(
    address: string,
    instanceGUID: string
  ): Promise<string> {
    return await this._smartComponentService.getPeaksDataCSV(
      address,
      instanceGUID,
      this.startDate.toISOString(),
      this.endDate.toISOString()
    );
  }

  /**
   * Get yearly for month consumption
   * @param address
   * @param instanceGUID
   * @returns
   */
  protected async getYearlyForMonthConsumption(
    address: string,
    instanceGUID: string
  ): Promise<GenericCounterGraphData> {
    return await this._smartComponentService.getYearlyForMonthConsumption(
      address,
      instanceGUID
    );
  }

  /**
   * Get summary yearly for month consumption
   * @param idEnvironment
   * @param idSmartComponentKind
   * @param skill
   * @returns
   */
  protected async getSummaryYearlyForMonthConsumption(
    idEnvironment: number,
    idSmartComponentKind: number,
    skill: string
  ): Promise<GenericCounterGraphData> {
    return await this._smartComponentService.getSummaryYearlyForMonthConsumption(
      idEnvironment,
      idSmartComponentKind,
      skill
    );
  }

  /**
   * Get summary yearly for month consumption csv
   * @param idEnvironment
   * @param idSmartComponentKind
   * @param skill
   * @returns
   */
  protected async getSummaryYearlyForMonthConsumptionCSV(
    idEnvironment: number,
    idSmartComponentKind: number,
    skill: string
  ) {
    return await this._smartComponentService.getSummaryYearlyForMonthConsumptionCSV(
      idEnvironment,
      idSmartComponentKind,
      skill
    );
  }

  /**
   * Get yearly for month consumption csv
   * @param address
   * @param instanceGUID
   * @returns
   */
  protected async getYearlyForMonthConsumptionCSV(
    address: string,
    instanceGUID: string
  ): Promise<string> {
    return await this._smartComponentService.getYearlyForMonthConsumptionCSV(
      address,
      instanceGUID
    );
  }

  /**
   * Load component procedure start
   */
  protected abstract onComponentLoad(): void;

  /**
   * Destroy component procedure
   */
  protected abstract onComponentDestroy(): void;
}
